import type { CleaningCompletionReportApartment } from '@/shared/api';
import { evidenceLabel } from './completionReportModel';

/**
 * Farben des Diagramms. ECharts nimmt keine CSS-Variablen; die Werte spiegeln `globals.css`
 * (forest, mint, line, ink-soft) und wandern nur gemeinsam mit ihr.
 */
const CHART = { forest: '#1e5b41', mint: '#b9dab7', line: '#e0d9cb', inkSoft: '#5e675f' } as const;

const nameOf = (apartment: CleaningCompletionReportApartment) =>
  apartment.operationalName ?? apartment.name;

const escape = (text: string) => text.replace(/[&<>"]/g, (char) => `&#${char.charCodeAt(0)};`);

/** Tooltip je Apartment mit dem Nachweis als „a von b“ – der Balken allein läse sich als Lücke. */
const tooltipFor = (apartment: CleaningCompletionReportApartment | undefined) =>
  apartment
    ? `${escape(nameOf(apartment))}<br/>Reinigungen: ${apartment.totals.cleaningCount}<br/>Fotonachweis: ${evidenceLabel(apartment.totals)}`
    : '';

/**
 * Balken je Apartment: abgeschlossene Reinigungen und davon mit vollständigem Nachweis — beides
 * Serverwerte, nichts im Browser gerechnet. Ohne Fotopflicht im Zeitraum fehlt die zweite Reihe;
 * ein Apartment ohne Fotopflicht bekommt dort keinen Balken statt einer 0, die wie „fehlt“ aussähe.
 */
export function completionChartOption(apartments: CleaningCompletionReportApartment[]) {
  const withEvidence = apartments.some((apartment) => apartment.totals.evidenceRequiredCount > 0);
  const series = [
    {
      name: 'Reinigungen',
      type: 'bar',
      data: apartments.map((apartment) => ({
        id: apartment.propertyId,
        value: apartment.totals.cleaningCount,
      })),
    },
    ...(withEvidence
      ? [
          {
            name: 'mit vollständigem Fotonachweis',
            type: 'bar',
            data: apartments.map((apartment) => ({
              id: apartment.propertyId,
              value:
                apartment.totals.evidenceRequiredCount > 0
                  ? apartment.totals.evidenceCompleteCount
                  : null,
            })),
          },
        ]
      : []),
  ];
  return {
    aria: { enabled: true, decal: { show: true } },
    color: [CHART.forest, CHART.mint],
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: { dataIndex: number }[]) =>
        tooltipFor(apartments[params[0]?.dataIndex ?? -1]),
    },
    legend: withEvidence ? { bottom: 0, textStyle: { color: CHART.inkSoft } } : undefined,
    grid: { left: 8, right: 16, top: 8, bottom: withEvidence ? 36 : 8, containLabel: true },
    xAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: CHART.inkSoft },
      splitLine: { lineStyle: { color: CHART.line } },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: apartments.map(nameOf),
      axisLabel: { color: CHART.inkSoft },
      axisLine: { lineStyle: { color: CHART.line } },
    },
    series,
  };
}

/** Der Satz für Vorleser und als Kurzfassung unter dem Diagramm. */
export function completionChartSentence(apartments: CleaningCompletionReportApartment[]): string {
  if (apartments.length === 0) return 'Keine Apartments im Zeitraum.';
  const top = [...apartments].sort((a, b) => b.totals.cleaningCount - a.totals.cleaningCount)[0]!;
  const missing = apartments.filter(
    (apartment) => apartment.totals.evidenceRequiredCount > apartment.totals.evidenceCompleteCount,
  );
  const required = apartments.some((apartment) => apartment.totals.evidenceRequiredCount > 0);
  return [
    `${apartments.length} ${apartments.length === 1 ? 'Apartment' : 'Apartments'}`,
    `am meisten Reinigungen: ${nameOf(top)} (${top.totals.cleaningCount})`,
    missing.length > 0
      ? `Fotonachweis unvollständig bei ${missing.map(nameOf).join(', ')}`
      : required
        ? 'alle verlangten Fotonachweise vollständig'
        : 'keine Fotonachweise verlangt',
  ].join(' · ');
}

/** Höhe nach Zahl der Apartments, damit Balken lesbar bleiben. */
export const completionChartHeight = (count: number) =>
  Math.max(160, Math.min(560, 48 + count * 40));
